import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  getPetDocuments, uploadOwnerDocument, deleteDocument,
  approveProvider, revokeProvider, documentUrl
} from '../services/petMediaApi';

const MAX_DOC_MB = 25;

const DOC_CATEGORIES = [
  { value: 'x-ray',        label: 'X-Ray / Imaging' },
  { value: 'lab-results',  label: 'Lab Results' },
  { value: 'prescription', label: 'Prescription' },
  { value: 'vaccination',  label: 'Vaccination Record' },
  { value: 'other',        label: 'Other' },
];

const CAT_ICONS = {
  'x-ray':        '🩻',
  'lab-results':  '🧪',
  'prescription': '💊',
  'vaccination':  '💉',
  'other':        '📄',
};

function fileSizeLabel(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Document row ──────────────────────────────────────────────
function DocRow({ doc, onDelete, onApprove, onRevoke }) {
  const [showAccess, setShowAccess] = useState(false);
  const [providerId, setProviderId] = useState('');

  const token    = localStorage.getItem('token');
  const viewHref = documentUrl(doc.filename);

  return (
    <div className="pm-doc-row">
      <div className="pm-doc-icon">{CAT_ICONS[doc.category] || '📄'}</div>

      <div className="pm-doc-body">
        <div className="pm-doc-name">
          <a
            href={viewHref}
            target="_blank"
            rel="noreferrer"
            onClick={e => {
              // Inject token into the request via fetch + blob URL
              e.preventDefault();
              fetch(viewHref, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
                .then(r => r.blob())
                .then(b => {
                  const url = URL.createObjectURL(b);
                  window.open(url, '_blank');
                  setTimeout(() => URL.revokeObjectURL(url), 10000);
                })
                .catch(() => toast.error('Failed to open document'));
            }}
          >
            {doc.originalName}
          </a>
        </div>
        <div className="pm-doc-meta">
          <span className="pm-pill grey">{DOC_CATEGORIES.find(c => c.value === doc.category)?.label || doc.category}</span>
          <span style={{ fontSize: 11, color: 'var(--text3, #a0aec0)' }}>{fileSizeLabel(doc.size)}</span>
          {doc.uploadedByRole === 'provider' && (
            <span className="pm-pill" style={{ background: '#ebf4ff', color: '#3182ce', border: '1px solid #bee3f8', fontSize: 11 }}>
              Provider upload
            </span>
          )}
          {doc.caption && <span style={{ fontSize: 12, color: 'var(--text2, #4a5568)' }}>{doc.caption}</span>}
        </div>

        {/* Approved providers list */}
        {doc.approvedProviders?.length > 0 && (
          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text3, #718096)' }}>
            Shared with {doc.approvedProviders.length} provider{doc.approvedProviders.length !== 1 ? 's' : ''}
            {' '}
            <button
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#667eea', fontSize: 12, padding: 0 }}
              onClick={() => setShowAccess(v => !v)}
            >
              {showAccess ? 'Hide' : 'Manage'}
            </button>
          </div>
        )}

        {showAccess && (
          <div style={{ marginTop: 8, background: '#f7fafc', borderRadius: 8, padding: 10 }}>
            {doc.approvedProviders.map(ap => (
              <div key={ap.providerId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 12 }}>
                <span style={{ fontFamily: 'monospace', color: '#4a5568' }}>{ap.providerId}</span>
                {!(doc.uploadedByRole === 'provider' && doc.uploadedById === ap.providerId) && (
                  <button
                    className="pm-btn pm-btn-ghost pm-btn-sm"
                    style={{ color: '#e53e3e', borderColor: '#e53e3e' }}
                    onClick={() => onRevoke(doc._id, ap.providerId)}
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}

            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <input
                className="pm-input pm-mono"
                value={providerId}
                onChange={e => setProviderId(e.target.value)}
                placeholder="Provider ID to grant access"
                style={{ flex: 1, fontSize: 12 }}
              />
              <button
                className="pm-btn pm-btn-primary pm-btn-sm"
                disabled={!providerId.trim()}
                onClick={() => { onApprove(doc._id, providerId.trim()); setProviderId(''); }}
              >
                Grant
              </button>
            </div>
          </div>
        )}

        {/* Quick grant access (show when no approved providers) */}
        {!showAccess && doc.approvedProviders?.length === 0 && (
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#667eea', fontSize: 12, padding: '4px 0', marginTop: 4 }}
            onClick={() => setShowAccess(true)}
          >
            + Grant provider access
          </button>
        )}
      </div>

      <button
        className="pm-btn pm-btn-ghost pm-btn-sm"
        style={{ color: '#e53e3e', borderColor: '#e53e3e', alignSelf: 'flex-start', flexShrink: 0 }}
        onClick={() => onDelete(doc._id)}
        title="Delete document"
      >
        🗑
      </button>
    </div>
  );
}

// ── Upload form ───────────────────────────────────────────────
function DocUpload({ petId, onUploaded }) {
  const inputRef  = useRef();
  const [file, setFile]     = useState(null);
  const [caption, setCaption] = useState('');
  const [category, setCategory] = useState('other');
  const [uploading, setUploading] = useState(false);

  const selectFile = f => {
    if (!f) return;
    if (f.size > MAX_DOC_MB * 1024 * 1024) {
      toast.error(`File must be under ${MAX_DOC_MB} MB`); return;
    }
    setFile(f);
  };

  const handleUpload = async e => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('document', file);
      fd.append('caption', caption);
      fd.append('category', category);
      const res = await uploadOwnerDocument(petId, fd);
      toast.success('Document uploaded');
      onUploaded(res.data.media);
      setFile(null); setCaption(''); setCategory('other');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  return (
    <form onSubmit={handleUpload} className="pm-doc-upload">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="pm-btn pm-btn-secondary"
          onClick={() => inputRef.current?.click()}
        >
          📎 {file ? file.name : 'Choose File'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          style={{ display: 'none' }}
          onChange={e => selectFile(e.target.files[0])}
        />

        <select className="pm-input" value={category} onChange={e => setCategory(e.target.value)} style={{ width: 'auto' }}>
          {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>

        <input
          className="pm-input"
          value={caption}
          onChange={e => setCaption(e.target.value)}
          placeholder="Description (optional)"
          maxLength={256}
          style={{ flex: 1, minWidth: 140 }}
        />

        <button type="submit" className="pm-btn pm-btn-primary" disabled={!file || uploading}>
          {uploading ? 'Uploading…' : '⬆ Upload'}
        </button>
      </div>
      {file && (
        <p style={{ margin: '6px 0 0', fontSize: 12, color: '#718096' }}>
          {file.name} · {fileSizeLabel(file.size)}
        </p>
      )}
    </form>
  );
}

// ── Main export ───────────────────────────────────────────────
export default function PetDocumentManager({ petId }) {
  const [docs, setDocs]       = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPetDocuments(petId);
      setDocs(res.data.documents);
    } catch { toast.error('Failed to load documents'); }
    finally { setLoading(false); }
  }, [petId]);

  useEffect(() => { load(); }, [load]);

  const handleUploaded = doc => setDocs(ds => [doc, ...ds]);

  const handleDelete = async mediaId => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    try {
      await deleteDocument(petId, mediaId);
      setDocs(ds => ds.filter(d => d._id !== mediaId));
      toast.success('Document deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleApprove = async (mediaId, providerId) => {
    try {
      await approveProvider(petId, mediaId, providerId);
      setDocs(ds => ds.map(d =>
        d._id === mediaId
          ? { ...d, approvedProviders: [...(d.approvedProviders || []), { providerId }] }
          : d
      ));
      toast.success('Access granted');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleRevoke = async (mediaId, providerId) => {
    try {
      await revokeProvider(petId, mediaId, providerId);
      setDocs(ds => ds.map(d =>
        d._id === mediaId
          ? { ...d, approvedProviders: d.approvedProviders.filter(ap => ap.providerId !== providerId) }
          : d
      ));
      toast.success('Access revoked');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div className="pm-section">
      <p style={{ fontSize: 13, color: 'var(--text3, #718096)', marginBottom: 12 }}>
        Upload medical records for your pet. You control which providers can see each document.
        Providers can also upload documents — you will see all of them.
      </p>

      <DocUpload petId={petId} onUploaded={handleUploaded} />

      {loading ? (
        <div className="pm-loading">Loading documents…</div>
      ) : docs.length === 0 ? (
        <div className="pm-empty">
          <span className="pm-empty-icon">📁</span>
          <p>No documents yet.</p>
        </div>
      ) : (
        <div className="pm-doc-list">
          {docs.map(doc => (
            <DocRow
              key={doc._id}
              doc={doc}
              onDelete={handleDelete}
              onApprove={handleApprove}
              onRevoke={handleRevoke}
            />
          ))}
        </div>
      )}
    </div>
  );
}
